import React, { useState } from 'react';
import { Button, TextInput, Select } from '@etherisc/ui-kit';
import { ClientSelect } from './ClientSelect';
import { CostCenterSelect } from './CostCenterSelect';
import { useRecordPaymentReceived } from '../../hooks/insurance/useInsurancePremium';
import type { PaymentReceivedRequest, InsuranceJournal } from '../../types/Insurance';

interface PaymentFormData {
  clientId: string;
  costCenterId: string;
  amount: string;
  currency: string;
  provider: string;
  externalPaymentId: string;
  batchId?: string;
  description?: string;
}

interface PaymentReceivedFormProps {
  onSuccess?: (journal: InsuranceJournal) => void;
  onCancel: () => void;
  initialData?: Partial<PaymentFormData>;
}

export const PaymentReceivedForm: React.FC<PaymentReceivedFormProps> = ({ 
  onSuccess, 
  onCancel,
  initialData 
}) => {
  const [formData, setFormData] = useState<PaymentFormData>({
    clientId: initialData?.clientId || '',
    costCenterId: initialData?.costCenterId || '',
    amount: initialData?.amount || '',
    currency: initialData?.currency || 'EUR',
    provider: initialData?.provider || 'stripe',
    externalPaymentId: initialData?.externalPaymentId || '',
    batchId: initialData?.batchId || '',
    description: initialData?.description || ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const recordPaymentReceived = useRecordPaymentReceived();

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.clientId) {
      newErrors.clientId = 'Client is required';
    }

    if (!formData.costCenterId) {
      newErrors.costCenterId = 'Policy/Cost Center is required';
    }

    if (!formData.amount) {
      newErrors.amount = 'Payment amount is required';
    } else {
      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount <= 0) {
        newErrors.amount = 'Payment amount must be a positive number';
      }
    }

    if (!formData.currency) {
      newErrors.currency = 'Currency is required';
    } else if (!/^[A-Z]{3}$/.test(formData.currency)) {
      newErrors.currency = 'Currency must be a valid 3-letter code (e.g., EUR, USD)';
    }

    if (!formData.provider) {
      newErrors.provider = 'Payment provider is required';
    }

    if (!formData.externalPaymentId) {
      newErrors.externalPaymentId = 'External payment ID is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const request: PaymentReceivedRequest = {
        clientId: formData.clientId,
        costCenterId: formData.costCenterId,
        amount: formData.amount,
        currency: formData.currency,
        provider: formData.provider,
        externalPaymentId: formData.externalPaymentId,
        batchId: formData.batchId || undefined,
        description: formData.description || undefined
      };

      const result = await recordPaymentReceived.mutateAsync(request);
      
      if (onSuccess) {
        onSuccess(result);
      }
      
      // Reset form
      setFormData({
        clientId: '',
        costCenterId: '',
        amount: '',
        currency: 'EUR',
        provider: 'stripe',
        externalPaymentId: '',
        batchId: '',
        description: ''
      });
      
    } catch (error) {
      console.error('Failed to record payment received:', error);
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to record payment received' });
    }
  };

  const updateFormData = (field: keyof PaymentFormData) => (value: string | React.ChangeEvent<HTMLInputElement>) => {
    const actualValue = typeof value === 'string' ? value : value.target.value;
    setFormData(prev => ({ ...prev, [field]: actualValue }));
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ClientSelect 
          label="Client" 
          value={formData.clientId} 
          onChange={updateFormData('clientId')}
          required
        />
        {errors.clientId && (
          <div className="text-red-500 text-sm mt-1">{errors.clientId}</div>
        )}
        
        <CostCenterSelect 
          label="Policy/Cost Center" 
          clientId={formData.clientId}
          value={formData.costCenterId} 
          onChange={updateFormData('costCenterId')}
          required
        />
        {errors.costCenterId && (
          <div className="text-red-500 text-sm mt-1">{errors.costCenterId}</div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TextInput 
          label="Payment Amount" 
          type="text"
          value={formData.amount} 
          onChange={updateFormData('amount')}
          placeholder="0.00"
          required
        />
        {errors.amount && (
          <div className="text-red-500 text-sm mt-1">{errors.amount}</div>
        )}
        
        <Select 
          label="Currency" 
          value={formData.currency} 
          onValueChange={updateFormData('currency')}
          required
          options={[
            { value: 'EUR', label: 'EUR' },
            { value: 'USD', label: 'USD' },
            { value: 'GBP', label: 'GBP' }
          ]}
        />
        {errors.currency && (
          <div className="text-red-500 text-sm mt-1">{errors.currency}</div>
        )}
        
        <Select 
          label="Payment Provider" 
          value={formData.provider} 
          onValueChange={updateFormData('provider')}
          required
          options={[
            { value: 'stripe', label: 'Stripe' },
            { value: 'paypal', label: 'PayPal' },
            { value: 'bank_transfer', label: 'Bank Transfer' },
            { value: 'check', label: 'Check' }
          ]}
        />
        {errors.provider && (
          <div className="text-red-500 text-sm mt-1">{errors.provider}</div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextInput 
          label="External Payment ID" 
          type="text"
          value={formData.externalPaymentId} 
          onChange={updateFormData('externalPaymentId')}
          placeholder="pi_1234567890 or transaction ID"
          required
        />
        {errors.externalPaymentId && (
          <div className="text-red-500 text-sm mt-1">{errors.externalPaymentId}</div>
        )}
        
        <TextInput 
          label="Batch ID (Optional)" 
          type="text"
          value={formData.batchId || ''} 
          onChange={updateFormData('batchId')}
          placeholder="batch_1234567890"
        />
      </div>

      <TextInput 
        label="Description (Optional)" 
        type="text"
        value={formData.description || ''} 
        onChange={updateFormData('description')}
        placeholder="Additional notes about this payment..."
      />

      {errors.submit && (
        <div className="text-red-500 text-sm p-3 bg-red-50 rounded-md">
          {errors.submit}
        </div>
      )}

      <div className="flex gap-2 justify-end pt-4">
        <Button 
          variant="outline" 
          onClick={onCancel}
          disabled={recordPaymentReceived.isPending}
        >
          Cancel
        </Button>
        <Button 
          type="submit"
          disabled={recordPaymentReceived.isPending}
        >
          {recordPaymentReceived.isPending ? 'Recording...' : 'Record Payment Received'}
        </Button>
      </div>
    </form>
  );
};
